import { useEffect, useState } from 'react'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { AdjustmentsHorizontalIcon, ChevronDownIcon, MegaphoneIcon, PlusIcon } from '@heroicons/react/20/solid'
import { AdManagementService, type MetaAdset, type MetaCampaign } from '../../client'
import { statusBadgeClass } from '../../lib/adPromotion'

const NEW = '__new__'

const TRIGGER =
  'group flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none'
const PANEL =
  'z-[60] w-(--button-width) divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg [--anchor-gap:4px] [--anchor-max-height:12rem]'
const OPTION =
  'flex w-full cursor-pointer items-center justify-between px-3 py-2.5 text-left text-sm transition hover:bg-gray-50 data-selected:bg-indigo-50'
const ADD = 'flex w-full cursor-pointer items-center gap-2 px-3 py-2.5 text-left text-sm text-indigo-600 transition hover:bg-indigo-50 data-selected:bg-indigo-50 data-selected:font-medium'
const CHEVRON = 'ml-2 size-4 shrink-0 text-gray-400 transition-transform group-data-open:rotate-180'

function Heading({ icon: Icon, children }: { icon: typeof MegaphoneIcon; children: string }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <Icon className="size-5 text-gray-500" />
      <h4 className="text-sm font-semibold text-gray-900">{children}</h4>
    </div>
  )
}

function Status({ status }: { status?: string | null }) {
  if (!status) return null
  return (
    <span className={`ml-3 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(status)}`}>
      {status}
    </span>
  )
}

type Props = {
  brandId: number
  adAccountId: string
  newCampaign: boolean
  campaignId: string
  newAdset: boolean
  adsetId: string
  onSelectCampaign: (campaign: MetaCampaign | null) => void
  onNewCampaign: () => void
  onSelectAdset: (adset: MetaAdset | null) => void
  onNewAdset: () => void
}

export default function ExistingCampaignSelector({
  brandId,
  adAccountId,
  newCampaign,
  campaignId,
  newAdset,
  adsetId,
  onSelectCampaign,
  onNewCampaign,
  onSelectAdset,
  onNewAdset,
}: Props) {
  const [campaigns, setCampaigns] = useState<MetaCampaign[]>([])
  const [loaded, setLoaded] = useState<{ campaignId: string; adsets: MetaAdset[] }>({ campaignId: '', adsets: [] })
  const [loadingCampaigns, setLoadingCampaigns] = useState(true)
  const [error, setError] = useState('')

  const adsets = loaded.campaignId === campaignId ? loaded.adsets : []
  const loadingAdsets = Boolean(campaignId) && loaded.campaignId !== campaignId
  const campaign = campaigns.find(({ id }) => id === campaignId)
  const adset = adsets.find(({ id }) => id === adsetId)

  useEffect(() => {
    if (!adAccountId) return

    AdManagementService.getCampaigns(brandId, adAccountId)
      .then(setCampaigns)
      .catch(() => setError('Failed to load campaigns.'))
      .finally(() => setLoadingCampaigns(false))
  }, [brandId, adAccountId])

  useEffect(() => {
    if (!campaignId) return

    AdManagementService.getAdsets(campaignId, brandId)
      .then((fetched) => setLoaded({ campaignId, adsets: fetched }))
      .catch(() => {
        setError('Failed to load ad sets.')
        setLoaded({ campaignId, adsets: [] })
      })
  }, [brandId, campaignId])

  function pickCampaign(value: string) {
    if (value === NEW) onNewCampaign()
    else onSelectCampaign(campaigns.find(({ id }) => id === value) ?? null)
  }

  function pickAdset(value: string) {
    if (value === NEW) onNewAdset()
    else onSelectAdset(adsets.find(({ id }) => id === value) ?? null)
  }

  return (
    <div className="space-y-6">
      <div>
        <Heading icon={MegaphoneIcon}>Campaign</Heading>

        {loadingCampaigns ? (
          <div className="h-10 animate-pulse rounded-lg bg-gray-200" />
        ) : (
          <Listbox value={newCampaign ? NEW : campaignId} onChange={pickCampaign}>
            <ListboxButton className={TRIGGER}>
              {newCampaign ? (
                <span className="flex items-center gap-1.5 font-medium text-indigo-600">
                  <PlusIcon className="size-4 shrink-0" /> New campaign
                </span>
              ) : campaign ? (
                <span className="truncate text-gray-800">
                  {campaign.name} <span className="text-xs text-gray-400">({campaign.objective})</span>
                </span>
              ) : (
                <span className="text-gray-400">Select a campaign</span>
              )}
              <ChevronDownIcon className={CHEVRON} />
            </ListboxButton>

            <ListboxOptions anchor="bottom start" className={PANEL}>
              <ListboxOption value="" className={`${OPTION} text-gray-400`}>
                <span>Select a campaign</span>
                <span className="text-xs">
                  {campaigns.length} campaign{campaigns.length === 1 ? '' : 's'} found
                </span>
              </ListboxOption>

              {campaigns.map((option) => (
                <ListboxOption key={option.id} value={option.id} className={OPTION}>
                  <span className="truncate text-gray-800">
                    {option.name} <span className="text-xs text-gray-400">({option.objective})</span>
                  </span>
                  <Status status={option.status} />
                </ListboxOption>
              ))}

              <ListboxOption value={NEW} className={ADD}>
                <PlusIcon className="size-4 shrink-0" /> New campaign
              </ListboxOption>
            </ListboxOptions>
          </Listbox>
        )}
      </div>

      {campaignId && (
        <div>
          <Heading icon={AdjustmentsHorizontalIcon}>Ad Set</Heading>

          {loadingAdsets ? (
            <div className="h-10 animate-pulse rounded-lg bg-gray-200" />
          ) : (
            <Listbox value={newAdset ? NEW : adsetId} onChange={pickAdset}>
              <ListboxButton className={TRIGGER}>
                {newAdset ? (
                  <span className="flex items-center gap-1.5 font-medium text-indigo-600">
                    <PlusIcon className="size-4 shrink-0" /> New Ad Set
                  </span>
                ) : adset ? (
                  <span className="truncate text-gray-800">{adset.name}</span>
                ) : (
                  <span className="text-gray-400">Select an ad set</span>
                )}
                <ChevronDownIcon className={CHEVRON} />
              </ListboxButton>

              <ListboxOptions anchor="bottom start" className={PANEL}>
                <ListboxOption value="" className={`${OPTION} text-gray-400`}>
                  <span>Select an ad set</span>
                  <span className="text-xs">
                    {adsets.length} ad set{adsets.length === 1 ? '' : 's'} found
                  </span>
                </ListboxOption>

                {adsets.map((option) => (
                  <ListboxOption key={option.id} value={option.id} className={OPTION}>
                    <span className="truncate text-gray-800">{option.name}</span>
                    <Status status={option.status} />
                  </ListboxOption>
                ))}

                <ListboxOption value={NEW} className={ADD}>
                  <PlusIcon className="size-4 shrink-0" /> New Ad Set
                </ListboxOption>
              </ListboxOptions>
            </Listbox>
          )}
        </div>
      )}

      {error && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">{error}</p>}
    </div>
  )
}
